import { useCallback, useEffect, useMemo, useRef } from 'react';

import { StateNode, Workflow, WorkflowInvoke } from '@sierralabs/workflow';

import {
  useApiDelete,
  useApiGet,
  useApiPatch,
  useApiPost
} from '../../common/api/Api.hooks';
import { ApiParams, FetchError } from '../../common/api/api';
import { useTimeout } from '../../common/timeout.hook';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { useUserContext } from '../../user/User.context';
import { CapaField, CapaFieldLabels, CapaFields } from '../capa/Capa.fields';
import {
  RiskField,
  RiskFieldLabels,
  RiskFields
} from '../risk-management/Risk.fields';
import { ScarField, ScarFieldLabels, ScarFields } from '../scar/scar.fields';
import { Task } from '../tasks/Task.interface';
import { parseTaskStatus } from '../tasks/task.helper';
import { TaskTypeEntity } from '../task-types/TaskType.interface';
import {
  TrainingField,
  TrainingFieldLabels,
  TrainingFields
} from '../training/Training.fields';
import {
  WorkflowDto,
  WorkflowEntity,
  WorkflowListItem,
  WorkflowStatusLog,
  WorkflowVersion,
  WorkflowInvokeEvent,
  WorkflowInvokeAction,
  WorkflowTaskTypeEntity
} from './Workflow.interface';
import {
  SupplierField,
  SupplierFieldLabels,
  SupplierFields
} from '../supplier/Supplier.fields';
import {
  SporField,
  SporFieldLabels,
  SporFields
} from '../purchase/Spor.fields';
import {
  DocumentField,
  DocumentFields,
  DocumentFieldLabels
} from '../documents/Document.fields';
import { Group } from '../groups/Group.interface';
import * as PRODUCT from '../products/Product.fields';
import * as HAZARD from '../hazards/Hazard.fields';
import { useWorkflowContext } from './Workflow.context';
import {
  ChangeOrderField,
  ChangeOrderFieldLabels,
  ChangeOrderFields
} from '../changeOrder/ChangeOrder.fields';

export function getStatusLabel(status: string) {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
  }
}

export function useCreateWorkflow() {
  const { data, isLoading, error, post } = useApiPost<WorkflowDto>('workflows');
  return {
    createdWorkflow: data,
    isCreating: isLoading,
    createError: error,
    createWorkflow: post
  };
}

export function useFindWorkflows() {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<WorkflowDto>>('workflows');
  return {
    workflowList: data,
    isLoading,
    loadingError: error,
    findWorkflows: get
  };
}

export function useFindWorkflowList() {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<WorkflowListItem>>('workflows/list');
  return {
    workflowList: data,
    isLoading,
    loadingError: error,
    findWorkflows: get
  };
}

export function useFindWorkflow(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<WorkflowDto>(
    `workflows/${id}`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { workflow: data, isLoading, loadingError: error, findWorkflow: get };
}

export function useFindWorkflowVersionByEntity(entity: WorkflowEntity) {
  const { data, isLoading, error, get } = useApiGet<WorkflowVersion>(
    `workflows/version/${entity}`
  );
  return {
    workflowVersion: data,
    isLoading,
    loadingError: error,
    findWorkflowVersion: get
  };
}

export function useFindWorkflowVersionByTaskType(taskType: string) {
  const { data, isLoading, error, get } = useApiGet<WorkflowVersion>(
    `workflows/version/task-type/${taskType}`
  );
  return {
    workflowVersion: data,
    isLoading,
    loadingError: error,
    findWorkflowVersion: get
  };
}

export function useFindWorkflowsByTaskTypeTarget(targetEntity: TaskTypeEntity) {
  const { data, isLoading, error, get } = useApiGet<WorkflowDto[]>(
    `workflows/task-type/${targetEntity}`
  );
  return {
    workflows: data,
    isLoading,
    loadingError: error,
    findWorkflows: get
  };
}

export function useUpdateWorkflow(id: number | undefined) {
  const { data, isLoading, error, patch } = useApiPatch<WorkflowDto>(
    `workflows/${id}`
  );
  return {
    updatedWorkflow: data,
    isUpdating: isLoading,
    updateError: error,
    updateWorkflow: patch
  };
}

export function useDeleteWorkflow(id: number | undefined) {
  const { data, isLoading, error, del } = useApiDelete<WorkflowDto>(
    `workflows/${id}`
  );
  return {
    deletedWorkflow: data,
    deletingWorkflow: isLoading,
    deletedWorkflowError: error,
    deleteWorkflow: del
  };
}

export function useUpdateWorkflowConfig(id: number | undefined) {
  const { data, isLoading, error, patch } = useApiPatch<WorkflowDto>(
    `workflows/${id}/config`
  );
  return {
    updatedWorkflow: data,
    isUpdating: isLoading,
    updateError: error,
    updateWorkflowConfig: patch
  };
}

export function useRevertWorkflow(id: number) {
  const { data, isLoading, error, post } = useApiPost<WorkflowDto>(
    `workflows/${id}/revert`
  );
  return { workflow: data, isLoading, error, revert: post };
}

export function useGetStatusLogs(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<WorkflowStatusLog>
  >(`workflows/${id}/status-log`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { workflowStatusLogs: data, isLoading, error, get };
}

export function useGetWorkflowVersions(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<WorkflowVersion[]>(
    `workflows/${id}/versions`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { workflowVersions: data, isLoading, error, get };
}

/**
 * Replace old `rootNode` from status if it does not match.
 */
export function parseWorkflowStatus(status?: string, workflow?: Workflow) {
  if (typeof status === 'undefined' || !workflow) {
    return status;
  }
  // prepend rootNode to status value if it does not match
  const rootNode = `/${workflow.stateNode.name}/`;
  const taskStatus = status.startsWith(rootNode)
    ? status
    : rootNode + parseTaskStatus(status);
  return taskStatus;
}

/**
 * Get StateNode based on `task.status` (wrapper for `workflow.getState(status)`).
 *
 * Handles possible old `rootNode` values when Workflow gets renamed.
 */
export function getStateNodeByStatus(
  status?: string,
  workflow?: Workflow
): StateNode | undefined {
  const taskStatus = parseWorkflowStatus(status, workflow);
  return workflow && typeof taskStatus === 'string'
    ? workflow.getState(taskStatus)
    : undefined;
}

// useMemo wrapper for getStateNodeByStatus
export function useStateNodeByStatus(
  status?: string,
  workflow?: Workflow
): StateNode | undefined {
  return useMemo(() => {
    return getStateNodeByStatus(status, workflow);
  }, [status, workflow]);
}

export type WorkflowField<Entity extends WorkflowTaskTypeEntity> =
  | 'assignSubTasks'
  | (Entity extends WorkflowEntity.Capa | TaskTypeEntity.Capa
      ? CapaField
      : Entity extends WorkflowEntity.File | TaskTypeEntity.File
      ? DocumentField
      : Entity extends WorkflowEntity.Risk | TaskTypeEntity.Risk
      ? RiskField
      : Entity extends WorkflowEntity.Training | TaskTypeEntity.Training
      ? TrainingField
      : Entity extends WorkflowEntity.Supplier | TaskTypeEntity.Supplier
      ? SupplierField
      : Entity extends WorkflowEntity.Scar | TaskTypeEntity.Scar
      ? ScarField
      : Entity extends WorkflowEntity.Spor | TaskTypeEntity.Spor
      ? SporField
      : Entity extends WorkflowEntity.Product | TaskTypeEntity.Product
      ? PRODUCT.ProductField
      : Entity extends WorkflowEntity.UserNeed | TaskTypeEntity.UserNeed
      ? PRODUCT.ProductUserNeedField
      : Entity extends WorkflowEntity.DesignInput | TaskTypeEntity.DesignInput
      ? PRODUCT.ProductDesignInputField
      : Entity extends WorkflowEntity.DesignOutput | TaskTypeEntity.DesignOutput
      ? PRODUCT.ProductDesignOutputField
      : Entity extends WorkflowEntity.DesignSpec | TaskTypeEntity.DesignSpec
      ? PRODUCT.ProductDesignSpecField
      : Entity extends
          | WorkflowEntity.DesignVerification
          | TaskTypeEntity.DesignVerification
      ? PRODUCT.ProductVerificationField
      : Entity extends
          | WorkflowEntity.DesignValidation
          | TaskTypeEntity.DesignValidation
      ? PRODUCT.ProductValidationField
      : Entity extends WorkflowEntity.Hazard | TaskTypeEntity.Hazard
      ? HAZARD.HazardField
      : Entity extends WorkflowEntity.ChangeOrder | TaskTypeEntity.ChangeOrder
      ? ChangeOrderField
      : string);

export type WorkflowFieldLabel<Entity extends WorkflowTaskTypeEntity> =
  Entity extends WorkflowEntity.Capa | TaskTypeEntity.Capa
    ? typeof CapaFieldLabels
    : Entity extends WorkflowEntity.File | TaskTypeEntity.File
    ? typeof DocumentFieldLabels
    : Entity extends WorkflowEntity.Risk | TaskTypeEntity.Risk
    ? typeof RiskFieldLabels
    : Entity extends WorkflowEntity.Training | TaskTypeEntity.Training
    ? typeof TrainingFieldLabels
    : Entity extends WorkflowEntity.Supplier | TaskTypeEntity.Supplier
    ? typeof SupplierFieldLabels
    : Entity extends WorkflowEntity.Scar | TaskTypeEntity.Scar
    ? typeof ScarFieldLabels
    : Entity extends WorkflowEntity.Spor | TaskTypeEntity.Spor
    ? typeof SporFieldLabels
    : Entity extends WorkflowEntity.Product | TaskTypeEntity.Product
    ? typeof PRODUCT.ProductFieldLabels
    : Entity extends WorkflowEntity.UserNeed | TaskTypeEntity.UserNeed
    ? typeof PRODUCT.ProductUserNeedFieldLabels
    : Entity extends WorkflowEntity.DesignInput | TaskTypeEntity.DesignInput
    ? typeof PRODUCT.ProductDesignInputFieldLabels
    : Entity extends WorkflowEntity.DesignOutput | TaskTypeEntity.DesignOutput
    ? typeof PRODUCT.ProductDesignOutputFieldLabels
    : Entity extends WorkflowEntity.DesignSpec | TaskTypeEntity.DesignSpec
    ? typeof PRODUCT.ProductDesignSpecFieldLabels
    : Entity extends
        | WorkflowEntity.DesignVerification
        | TaskTypeEntity.DesignVerification
    ? typeof PRODUCT.ProductVerificationFieldLabels
    : Entity extends
        | WorkflowEntity.DesignValidation
        | TaskTypeEntity.DesignValidation
    ? typeof PRODUCT.ProductValidationFieldLabels
    : Entity extends WorkflowEntity.Hazard | TaskTypeEntity.Hazard
    ? typeof HAZARD.HazardFieldLabels
    : Entity extends WorkflowEntity.ChangeOrder | TaskTypeEntity.ChangeOrder
    ? typeof ChangeOrderFieldLabels
    : object;

export function useWorkflowFields<Entity extends WorkflowTaskTypeEntity>(
  entity: Entity
) {
  const fields: WorkflowField<Entity>[] = useMemo(() => {
    switch (entity) {
      case WorkflowEntity.Capa:
      case TaskTypeEntity.Capa:
        return CapaFields;
      case WorkflowEntity.File:
      case TaskTypeEntity.File:
        return DocumentFields;
      case WorkflowEntity.Risk:
      case TaskTypeEntity.Risk:
        return RiskFields;
      case WorkflowEntity.Training:
      case TaskTypeEntity.Training:
        return TrainingFields;
      case WorkflowEntity.Supplier:
      case TaskTypeEntity.Supplier:
        return SupplierFields;
      case WorkflowEntity.Scar:
      case TaskTypeEntity.Scar:
        return ScarFields;
      case WorkflowEntity.Spor:
      case TaskTypeEntity.Spor:
        return SporFields;
      case WorkflowEntity.Product:
      case TaskTypeEntity.Product:
        return PRODUCT.ProductFields;
      case WorkflowEntity.UserNeed:
      case TaskTypeEntity.UserNeed:
        return PRODUCT.ProductUserNeedFields;
      case WorkflowEntity.DesignInput:
      case TaskTypeEntity.DesignInput:
        return PRODUCT.ProductDesignInputFields;
      case WorkflowEntity.DesignOutput:
      case TaskTypeEntity.DesignOutput:
        return PRODUCT.ProductDesignOutputFields;
      case WorkflowEntity.DesignSpec:
      case TaskTypeEntity.DesignSpec:
        return PRODUCT.ProductDesignSpecFields;
      case WorkflowEntity.DesignVerification:
      case TaskTypeEntity.DesignVerification:
        return PRODUCT.ProductVerificationFields;
      case WorkflowEntity.DesignValidation:
      case TaskTypeEntity.DesignValidation:
        return PRODUCT.ProductValidationFields;
      case WorkflowEntity.Hazard:
      case TaskTypeEntity.Hazard:
        return HAZARD.HazardFields;
      case WorkflowEntity.Cause:
      case TaskTypeEntity.Cause:
        return HAZARD.CauseInputFields;
      case WorkflowEntity.Control:
      case TaskTypeEntity.Control:
        return HAZARD.ControlInputFields;
      case WorkflowEntity.Action:
      case TaskTypeEntity.Action:
        return HAZARD.ActionInputFields;
      case WorkflowEntity.ChangeOrder:
      case TaskTypeEntity.ChangeOrder:
        return ChangeOrderFields;
      default:
        return [];
    }
  }, [entity]) as WorkflowField<Entity>[];

  const labels: WorkflowFieldLabel<Entity> = useMemo(() => {
    switch (entity) {
      case WorkflowEntity.Capa:
      case TaskTypeEntity.Capa:
        return CapaFieldLabels;
      case WorkflowEntity.File:
      case TaskTypeEntity.File:
        return DocumentFieldLabels;
      case WorkflowEntity.Risk:
      case TaskTypeEntity.Risk:
        return RiskFieldLabels;
      case WorkflowEntity.Training:
      case TaskTypeEntity.Training:
        return TrainingFieldLabels;
      case WorkflowEntity.Supplier:
      case TaskTypeEntity.Supplier:
        return SupplierFieldLabels;
      case WorkflowEntity.Scar:
      case TaskTypeEntity.Scar:
        return ScarFieldLabels;
      case WorkflowEntity.Spor:
      case TaskTypeEntity.Spor:
        return SporFieldLabels;
      case WorkflowEntity.Product:
      case TaskTypeEntity.Product:
        return PRODUCT.ProductFieldLabels;
      case WorkflowEntity.UserNeed:
      case TaskTypeEntity.UserNeed:
        return PRODUCT.ProductUserNeedFieldLabels;
      case WorkflowEntity.DesignInput:
      case TaskTypeEntity.DesignInput:
        return PRODUCT.ProductDesignInputFieldLabels;
      case WorkflowEntity.DesignOutput:
      case TaskTypeEntity.DesignOutput:
        return PRODUCT.ProductDesignOutputFieldLabels;
      case WorkflowEntity.DesignSpec:
      case TaskTypeEntity.DesignSpec:
        return PRODUCT.ProductDesignSpecFieldLabels;
      case WorkflowEntity.DesignVerification:
      case TaskTypeEntity.DesignVerification:
        return PRODUCT.ProductVerificationFieldLabels;
      case WorkflowEntity.DesignValidation:
      case TaskTypeEntity.DesignValidation:
        return PRODUCT.ProductValidationFieldLabels;
      case WorkflowEntity.Hazard:
      case TaskTypeEntity.Hazard:
        return HAZARD.HazardFieldLabels;
      case WorkflowEntity.Cause:
      case TaskTypeEntity.Cause:
        return HAZARD.CauseInputFieldLabels;
      case WorkflowEntity.Control:
      case TaskTypeEntity.Control:
        return HAZARD.ControlInputFieldLabels;
      case WorkflowEntity.Action:
      case TaskTypeEntity.Action:
        return HAZARD.ActionInputFieldLabels;
      case WorkflowEntity.ChangeOrder:
      case TaskTypeEntity.ChangeOrder:
        return ChangeOrderFieldLabels;
      default:
        return {};
    }
  }, [entity]) as WorkflowFieldLabel<Entity>;
  return { fields, labels };
}

export function useTaskWorkflow(
  task: Pick<Task, 'workflowId' | 'taskTypeName'> | undefined,
  params?: ApiParams
): Workflow | undefined {
  const { workflows, getWorkflow } = useWorkflowContext();
  return useMemo(
    // added workflows dependency due to initial useTaskWorkflow call may need
    // to fetch the workflow before it is available to be returned. Adding
    // workflows state causes memo to refresh when the workflow is fetched thus
    // returning the workflow requested.
    () => workflows && getWorkflow(task, params),
    [task, params, workflows, getWorkflow]
  );
}

export interface TaskEntityFieldsOptions {
  token?: string;
  strict?: boolean;
}

export function useTaskEntityFields<Entity extends WorkflowTaskTypeEntity>(
  task: Task | undefined,
  strictOrOptions: boolean | TaskEntityFieldsOptions = true
) {
  const options =
    typeof strictOrOptions === 'boolean'
      ? { strict: strictOrOptions }
      : strictOrOptions;
  const { strict, token } = options;
  const { user } = useUserContext();
  const [debounceError] = useTimeout();
  const { error, setError } = useWorkflowContext();
  const errorRef = useRef<FetchError | undefined>();
  const workflow: Workflow | undefined = useTaskWorkflow(
    task,
    // wrap with useMemo to avoid multiple fetch calls
    useMemo(() => (token ? { token } : undefined), [token])
  );
  const stateNode: StateNode | undefined = useStateNodeByStatus(
    task?.status,
    workflow
  );

  const getError = useCallback((): FetchError | undefined => {
    if (strict && !workflow) {
      const { type, taskTypeName } = task || {};
      const message: string = taskTypeName
        ? `No published workflow for "${taskTypeName}" type.`
        : type
        ? `No published workflow for "${type}" entity.`
        : 'No published workflow for this item.';
      // skip assignment if existing error has the same message
      if (errorRef.current?.message !== message) {
        errorRef.current = { message, statusCode: 409, error: 'Conflict' };
      }
    } else {
      errorRef.current = undefined;
    }
    return errorRef.current;
  }, [task, strict, workflow]);

  useEffect(() => {
    if (strict) {
      debounceError(() => setError(getError()), 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strict, setError, getError]);

  useEffect(() => {
    // if context error is removed but there is still an error
    if (!error && errorRef.current) {
      setError(errorRef.current);
    }
  }, [error, setError]);

  // disable field if loading, error, or is not part of allowedFields
  const isFieldDisabled = useCallback(
    (field: WorkflowField<Entity>): boolean => {
      if (!stateNode) {
        console.warn('isFieldDisabled error: No State Node found.', {
          field,
          workflow,
          taskStatus: task?.status
        });
        return true;
      }

      const { allowedFields = [], groupPermissions = [] } =
        stateNode.meta || {};
      const hasGroupPermissions: boolean =
        groupPermissions.length === 0 ||
        !!user?.groups?.some((group: Group) => {
          return groupPermissions.includes(group.id);
        });
      return !hasGroupPermissions || !allowedFields.includes(field);
    },
    [user, stateNode, task, workflow]
  );

  return { error, isFieldDisabled };
}

export function useWorkflowGroupPermissions(task: Task | undefined) {
  const { user } = useUserContext();
  const workflow = useTaskWorkflow(task);
  const stateNode = useStateNodeByStatus(task?.status, workflow);

  const permissions: number[] = useMemo(() => {
    return stateNode?.meta?.groupPermissions || [];
  }, [stateNode]);

  const hasGroupPermissions = useMemo(() => {
    // skip check if there were no configured permissions
    return (
      permissions.length === 0 ||
      (user?.groups || []).some(group => permissions.includes(group.id))
    );
  }, [permissions, user]);

  return { permissions, hasGroupPermissions };
}

export function useFindEventNames(entity: WorkflowEntity | undefined) {
  const { data, isLoading, error, get } = useApiGet<WorkflowInvokeEvent[]>(
    'workflows/event-names/' + entity
  );
  return {
    eventList: data,
    isLoading,
    loadingError: error,
    findEvents: get
  };
}

export function useWorkflowStatuses(
  workflowDto: Workflow | WorkflowDto | WorkflowVersion | undefined
): string[] {
  const workflow: Workflow | undefined = useMemo(() => {
    return workflowDto instanceof Workflow
      ? workflowDto
      : workflowDto?.config && new Workflow(workflowDto.config);
  }, [workflowDto]);

  return useMemo((): string[] => {
    if (!workflow) {
      return [];
    }
    // filter out root state node
    const rootNode: string = `/${workflow.stateNode.name}`;
    return workflow
      .getAllPaths()
      .filter((path: string) => path !== rootNode)
      .map(parseTaskStatus);
  }, [workflow]);
}

// get statuses for multiple workflow
export function useMultipleWorkflowStatuses(
  workflowsDto: WorkflowDto[] | undefined
): string[] {
  const workflows: Workflow[] | undefined = useMemo(() => {
    return (
      workflowsDto &&
      workflowsDto.map(workflowDto => {
        return workflowDto.config && new Workflow(workflowDto.config);
      })
    );
  }, [workflowsDto]);

  return useMemo((): string[] => {
    if (!workflows) {
      return [];
    }
    let paths: string[] = [];
    workflows.map(workflow => {
      // filter out root state node
      const rootNode: string = `/${workflow.stateNode.name}`;
      return paths.push(
        ...workflow
          .getAllPaths()
          .filter((path: string) => path !== rootNode)
          .map(parseTaskStatus)
      );
    });
    return Array.from(new Set(paths)); // remove duplicate paths
  }, [workflows]);
}

export function useWorkflowHasSubTasks(
  config: WorkflowDto['config'],
  workflowProp?: Workflow
) {
  const workflow: Workflow | undefined = useMemo(() => {
    return config ? new Workflow(config) : workflowProp;
  }, [config, workflowProp]);

  const pathsWithSubTasks: string[] = useMemo(() => {
    if (!workflow) {
      return [];
    }
    const paths: string[] = workflow.getAllPaths();
    return paths.filter((path: string) => {
      const { invoke } = workflow.getState(path) || {};
      const all: WorkflowInvoke[] = Array.isArray(invoke)
        ? invoke
        : invoke
        ? [invoke]
        : [];
      return all.some((invoke: WorkflowInvoke) => {
        return invoke?.src === WorkflowInvokeAction.CreateSubTasks;
      });
    });
  }, [workflow]);

  const statuses: string[] = useMemo(() => {
    return pathsWithSubTasks.map((path: string) => parseTaskStatus(path));
  }, [pathsWithSubTasks]);

  const hasSubTasks: boolean = useMemo(() => {
    return pathsWithSubTasks.length > 0;
  }, [pathsWithSubTasks]);

  return { statuses, hasSubTasks, pathsWithSubTasks };
}
